import { useCallback, useMemo, useState } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import { FilterAutocomplete, FilterItem } from "../users/UserFilters";
import { Button, Grid, IconButton, MenuItem, Select } from "@mui/material";
import { GET_PARAMETER_AUTOCOMPLETE } from "./userActivity.queries";
import dayjs from "dayjs";
import { DayPicker } from "react-day-picker";

export interface UserActivityFilterData {
  filters: Array<{
    field: string;
    operator: string;
    value: string
  }>;
  operator: 'OR' | 'AND';
}

interface UserActivityFilterProps {
  onFilter: (filter: UserActivityFilterData) => void;
}

export const UserActivityFilter: React.FC<UserActivityFilterProps> = (props: UserActivityFilterProps): JSX.Element => {
  const [filterItems, setFilterItems] = useState<Array<FilterItem>>([
    {
      field: 'createdAt',
      operator: 'equals',
      value: [
        dayjs(new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000)).startOf('day').toDate(),
        dayjs(new Date()).endOf('day').toDate()
      ],
      type: 'DateTime',
      kind: 'scalar'
    }
  ]);
  const [operator, setOperator] = useState<'AND' | 'OR'>('AND');

  const handleFilter = useCallback((event: any) => {
    const nonEmptyFilters = filterItems.filter((filter) => filter.field !== '');
    setFilterItems([
      ...nonEmptyFilters
    ]);
    props.onFilter({
      filters: [
        ...nonEmptyFilters.filter((item) => item.field !== 'createdAt').map((item) => ({
          field: item.field,
          operator: item.operator,
          value: `${(typeof item.value === 'object' && !(item.value instanceof Date) && !(item.value instanceof Array)) ? item.value.name : item.value}`
        })),
        ...(nonEmptyFilters.filter((item) => item.field === 'createdAt').map((item) => [
          {
            field: item.field,
            operator: '>=',
            value: dayjs((item.value as any)[0]).startOf('day').toDate().toISOString(),
          },
          {
            field: item.field,
            operator: '<=',
            value: dayjs((item.value as any)[1]).endOf('day').toDate().toISOString(),
          }
        ])).flat()
      ],
      operator,
    });
  }, [filterItems, operator, props]);

  const addEmptyCondition = useCallback((event: any) => {
    event.preventDefault();
    setFilterItems([
      ...filterItems,
      { field: '', operator: 'equals', value: '', type: '', kind: '' },
    ])
  }, [filterItems]);

  const updateFilterItem = useCallback((item: FilterItem, index: number) => {
    filterItems[index] = item;
    setFilterItems([
      ...filterItems,
    ]);
  }, [filterItems]);

  const deleteFilterItem = useCallback((index: number) => {
    setFilterItems(filterItems.filter((value, i) => i !== index));
  }, [filterItems]);

  const fields = useMemo(() => {
    return [
      { name: 'type', type: 'String', kind: 'scalar' },
      { name: 'parameters', type: 'String', kind: 'scalar' },
      { name: 'createdAt', type: 'DateTime', kind: 'scalar' },
    ];
  }, []);

  return (
    <div>
      <Button onClick={addEmptyCondition}>Add new condition</Button>
      {filterItems.map((item, index) => (
        <div key={`item-${index}-${item.field}`}>
          <Grid container>
            <Grid item xs={2}>
              <Select
                value={item.field}
                onChange={(event: any) => {
                  updateFilterItem({
                    ...item,
                    field: event.target.value,
                    type: fields.find((field: any) => field.name === event.target.value)?.type ?? '',
                    kind: fields.find((field: any) => field.name === event.target.value)?.kind ?? '',
                  }, index);
                }}
              >
                {fields.filter((field: any) => !field.isReference).map((field: any) => (<MenuItem key={field.name} value={field.name}>{field.name}</MenuItem>))}
              </Select>
            </Grid>
            <Grid item xs={2}>
              <Select
                value={item.operator}
                onChange={(event: any) => {
                  updateFilterItem({
                    ...item,
                    operator: event.target.value,
                  }, index);
                }}
              >
                {(item.field === 'type' || item.field === 'createdAt') && (<MenuItem value={'equals'}>equals</MenuItem>)}
                {(item.field === 'parameters') && (<MenuItem value={'like'}>contains</MenuItem>)}
                {/* {item.type === 'DateTime' && (<MenuItem value={'<='}>less than equal</MenuItem>)}
                {item.type === 'DateTime' && (<MenuItem value={'>='}>greater than equal</MenuItem>)} */}
              </Select>
            </Grid>
            <Grid item xs={7}>
              {item.field === 'type' && (
                <Select
                  value={item.value}
                  onChange={(event: any) => {
                    updateFilterItem({
                      ...item,
                      value: event.target.value,
                    }, index);
                  }}
                >
                  <MenuItem value={'app-startup'}>Opened the app</MenuItem>
                  <MenuItem value={'screen_view'}>Viewed screen</MenuItem>
                  <MenuItem value={'business_recommendation'}>Recommended a business</MenuItem>
                  <MenuItem value={'share_invitation_link'}>Shared invitation link</MenuItem>
                  <MenuItem value={'reply_to_message'}>Replied to a message in teams</MenuItem>
                  <MenuItem value={'message_sent'}>Sent a message in a team</MenuItem>
                  <MenuItem value={'referred_with_recommendation_points'}>Referred user recommended a business</MenuItem>
                </Select>
              )}
              {(item.field === 'parameters') && (
                <FilterAutocomplete
                  query={GET_PARAMETER_AUTOCOMPLETE}
                  value={{ displayName: `${item.value}`, name: `${item.value}` }}
                  label="Parameters"
                  noOptionsText="No parameters"
                  optionExtractor={(data) => data.parameterAutocomplete.map((d: any) => ({ displayName: d, name: d }))}

                  onChange={(value: any) => {
                    console.log('v', value);
                    updateFilterItem({
                      ...item,
                      value: value.name,
                    }, index);
                  }}
                />
              )}
              {item.type === 'DateTime' && (
                <div>
                  <Button variant="contained">
                    {item.value && (<span>{item.value ? dayjs((item.value as any)[0]).format('YYYY-MM-DD') : ''}</span>)}
                    {item.value && (<span>- {item.value ? dayjs((item.value as any)[1]).format('YYYY-MM-DD') : ''}</span>)}
                  </Button>
                  <DayPicker mode="range" selected={{
                    from: (item.value as any)[0],
                    to: (item.value as any)[1],
                  }}
                    onSelect={(value) => {
                      if (!value || !value.from || !value.to) {
                        return;
                      }
                      updateFilterItem({
                        ...item,
                        value: [dayjs(value.from).startOf('day').toDate(), dayjs(value.to).endOf('day').toDate()],
                      }, index);
                    }}
                  />
                </div>
                // <DatePicker
                //   defaultValue={item.value ? dayjs(item.value as Date) : undefined}
                //   onChange={(value) => {
                //     updateFilterItem({
                //       ...item,
                //       value: value?.toDate() ?? new Date(),
                //     }, index);
                //   }} />
              )}
            </Grid>
            <Grid item xs={1}>
              <IconButton aria-label="delete" onClick={() => deleteFilterItem(index)}>
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </div>
      ))}
      <div>
        Operator among conditions:
        <Select
          value={operator}
          onChange={(event: any) => setOperator(event.target.value)}
        >
          <MenuItem value={'AND'}>AND</MenuItem>
          <MenuItem value={'OR'}>OR</MenuItem>
        </Select>
      </div>
      <div>
        <Button variant="contained" onClick={handleFilter}>Filter</Button>
      </div>
    </div>
  );
};